/**
 * In-flight (or recently-uploaded) media tied to an optimistic
 * channel message. Lives on the community context's `medias$`
 * subject — mirrors PopshopRN's `GroupMessageMediaInfo`. The
 * `clientMessageId` ties the entry to its parent optimistic
 * message so multiple bubbles' previews render under the right
 * messages.
 *
 * The `source` is a discriminated union:
 *
 * - `local` — the file is queued or actively uploading. Carries the
 *   object URL + the original `File` (for retry) + a state machine
 *   (`queued | uploading | error`). The bubble paints a local
 *   preview tile with progress overlay.
 * - `remote` — the upload finished and we have a CDN URL +
 *   server-side media identifier. The bubble paints a normal
 *   preview tile. The entry stays in `medias$` until the Firestore
 *   confirmation lands (the confirmed message's `attachments`
 *   takes over) — that's the cleanup signal.
 *
 * Retry path: when a send fails after some uploads finished, the
 * `remote` entries survive and the next retry skips them — only the
 * `error`-state entries get re-uploaded. Matches upstream's
 * behavior.
 */

export enum ChannelMessageMediaType {
  Image = 'image',
  Video = 'video',
}

export interface ChannelMessageMediaMetadata {
  blurhash?: string;
  brightness?: number;
  dimensions?: {
    width: number;
    height: number;
  };
}

export type LocalMediaUploadState =
  | { status: 'queued' }
  | { status: 'uploading'; progress: number }
  | { status: 'error'; error: Error };

export interface LocalMediaSource {
  type: 'local';
  contentType: string;
  filename: string;
  /** Object URL (`URL.createObjectURL(file)`) for preview rendering. */
  localFileUri: string;
  /** The actual `File` — kept around so retries can re-upload. */
  localFile?: File;
  state: LocalMediaUploadState;
}

export interface RemoteMediaSource {
  type: 'remote';
  contentType: string;
  /** Server-side media item identifier (used to dedupe + attach to the message). */
  id: string;
  /** CDN URL. */
  url: string;
  metadata?: ChannelMessageMediaMetadata;
}

export type ChannelMessageMediaSource = LocalMediaSource | RemoteMediaSource;

export interface ChannelMessageMediaInfo {
  /** Parent optimistic message — keys the bubble lookup. */
  clientMessageId: string;
  /**
   * Unique id per media item. Doubles as the upload-task context
   * key so the bridge hook can find which entry to patch when an
   * event arrives from the file uploader.
   */
  key: string;
  type: ChannelMessageMediaType;
  source: ChannelMessageMediaSource;
}
